import { useParams } from '@tanstack/react-router';
import { useState, useContext, useMemo } from 'react';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import { CircularProgress, Chip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import PrimaryButton from 'src/components/common/PrimaryButton';
import DashboardLayout from 'src/layouts/dashboard/DashboardLayout';
import useDivision from 'src/api/organization/useDivision';
import useUpdateDivision from 'src/api/organization/useUpdateDivision';
import DivisionModal from 'src/components/division/DivisionModal';
import {
  useAliases,
  useUpsertAliases,
  useDeleteAlias,
} from 'src/api/core/aliases/useAliases';
import {
  AliasCrudTable,
  AliasItem,
} from 'src/components/common/AliasCrudTable';
import { AuthContext } from 'src/context/auth.context';
import { isAdmin } from 'src/utils/user';
import { useViewMode } from 'src/context/view-mode.context';
import {
  useIsModuleEnabled,
  ProductModule,
} from 'src/hooks/organization-config/useOrganizationEnabledModules';
import JSONEditor from 'src/components/common/JSONEditor';

const DivisionDetailPage = () => {
  const { divisionId } = useParams({
    from: '/authenticated/divisions/$divisionId',
  }) as { divisionId?: string };
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { currentUser } = useContext(AuthContext);
  const { viewMode } = useViewMode();
  const showMetadata = isAdmin(currentUser, viewMode);
  const { isModuleEnabled } = useIsModuleEnabled();
  const isRebatesEnabled = isModuleEnabled(ProductModule.REBATES);

  const { data, isLoading, error, refetch } = useDivision(
    divisionId || '',
    !!divisionId,
  );
  const {
    data: aliasData,
    isLoading: isLoadingAliases,
    refetch: refetchAliases,
  } = useAliases('division', divisionId || '', !!divisionId);
  const { mutateAsync: updateDivision } = useUpdateDivision(
    divisionId || '',
    () => refetch(),
  );
  const { mutateAsync: upsertAliases } = useUpsertAliases();
  const { mutateAsync: deleteAliasRequest } = useDeleteAlias();

  // Transform aliases data for AliasCrudTable
  // The API now returns addedByUserName directly
  const aliases: AliasItem[] = useMemo(() => {
    if (!aliasData?.aliases) return [];
    return aliasData.aliases.map((a) => ({
      id: a.id,
      alias: a.alias,
      addedByUserId: a.addedByUserId,
      addedByUserName: a.addedByUserName,
      createdAt: a.createdAt,
    }));
  }, [aliasData]);

  // Separator used in storage format
  const STORAGE_SEPARATOR = '|||';
  // Display separator (dot with spaces)
  const DISPLAY_SEPARATOR = ' . ';

  // Format alias for display: '|||' → ' . '
  const formatAlias = (alias: string): string => {
    return alias.replaceAll(STORAGE_SEPARATOR, DISPLAY_SEPARATOR);
  };

  // Parse user input back to storage format: ' . ' → '|||'
  // Also handles variations like '. ' or ' .' or just '.'
  const parseAlias = (userInput: string): string => {
    return userInput
      .replace(/\s*\.\s*/g, STORAGE_SEPARATOR)
      .replace(/^(\|\|\|)+/, '') // Remove leading separators
      .replace(/(\|\|\|)+$/, ''); // Remove trailing separators
  };

  const handleCreateAlias = async (alias: string) => {
    if (!divisionId) return;
    await upsertAliases({
      entityType: 'division',
      entityId: divisionId,
      aliases: [alias],
    });
    refetchAliases();
  };

  const handleUpdateAlias = async (
    _id: string,
    newAlias: string,
    oldAlias: string,
  ) => {
    if (!divisionId) return;
    // Delete the old alias and create the new one
    let deleteSucceeded = false;
    try {
      await deleteAliasRequest({
        entityType: 'division',
        entityId: divisionId,
        alias: oldAlias,
      });
      deleteSucceeded = true;
      await upsertAliases({
        entityType: 'division',
        entityId: divisionId,
        aliases: [newAlias],
      });
    } catch (error) {
      // If delete succeeded but upsert failed, attempt to restore the old alias
      if (deleteSucceeded) {
        try {
          await upsertAliases({
            entityType: 'division',
            entityId: divisionId,
            aliases: [oldAlias],
          });
        } catch (restoreError) {
          // If restore also fails, log but don't mask the original error
          console.error(
            'Failed to restore old alias after update failure:',
            restoreError,
          );
        }
      }
      // Rethrow the original error so it can be handled by the mutation's onError handler
      throw error;
    } finally {
      // Always refetch to ensure UI reflects the final state
      await refetchAliases();
    }
  };

  const handleDeleteAlias = async (aliasItem: AliasItem) => {
    if (!divisionId) return;
    await deleteAliasRequest({
      entityType: 'division',
      entityId: divisionId,
      alias: aliasItem.alias,
    });
    refetchAliases();
  };

  const handleEditClick = () => {
    setIsEditModalOpen(true);
  };

  const handleModalClose = () => {
    setIsEditModalOpen(false);
  };

  const handleModalSuccess = () => {
    refetch();
  };

  if (error) {
    return (
      <DashboardLayout>
        <MDBox p={3}>
          <Card sx={{ p: 3 }}>
            <MDTypography color="error" textAlign="center">
              Error loading division: {error.message}
            </MDTypography>
          </Card>
        </MDBox>
      </DashboardLayout>
    );
  }

  if (isLoading) {
    return (
      <DashboardLayout>
        <MDBox p={3}>
          <MDBox display="flex" justifyContent="center">
            <CircularProgress />
          </MDBox>
        </MDBox>
      </DashboardLayout>
    );
  }

  const division = data?.division;

  if (!division) {
    return (
      <DashboardLayout>
        <MDBox p={3}>
          <Card sx={{ p: 3 }}>
            <MDTypography color="error" textAlign="center">
              Division not found
            </MDTypography>
          </Card>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <MDBox p={3}>
        <Grid container spacing={3}>
          {/* Main Division Details */}
          <Grid item xs={12} md={8}>
            <Card sx={{ p: 3 }}>
              <MDBox
                mb={3}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <MDTypography variant="h5" mb={1}>
                  {division.name}
                </MDTypography>
                <PrimaryButton
                  onClick={handleEditClick}
                  startIcon={<EditIcon />}
                >
                  Edit Division
                </PrimaryButton>
              </MDBox>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={3}>
                  <MDBox mb={3}>
                    <MDTypography
                      variant="caption"
                      color="text"
                      fontWeight="bold"
                      display="block"
                    >
                      Internal Code
                    </MDTypography>
                    <MDTypography variant="body2">
                      {(division.metadata?.internal_code as string) || '-'}
                    </MDTypography>
                  </MDBox>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <MDBox mb={3}>
                    <MDTypography
                      variant="caption"
                      color="text"
                      fontWeight="bold"
                      display="block"
                    >
                      Description
                    </MDTypography>
                    <MDTypography
                      variant="body2"
                      sx={{ whiteSpace: 'pre-wrap' }}
                    >
                      {(division.metadata?.description as string) || '-'}
                    </MDTypography>
                  </MDBox>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <MDBox mb={3}>
                    <MDTypography
                      variant="caption"
                      color="text"
                      fontWeight="bold"
                      display="block"
                      mb={1}
                    >
                      Banners
                    </MDTypography>
                    <MDBox display="flex" gap={1} flexWrap="wrap">
                      {division.tags && division.tags.length > 0 ? (
                        division.tags.map((tag) => (
                          <Chip key={tag.id} label={tag.name} />
                        ))
                      ) : (
                        <MDTypography variant="body2" color="text.secondary">
                          -
                        </MDTypography>
                      )}
                    </MDBox>
                  </MDBox>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <MDBox mb={3}>
                    <MDTypography
                      variant="caption"
                      color="text"
                      fontWeight="bold"
                      display="block"
                    >
                      Geography
                    </MDTypography>
                    <MDTypography variant="body2">
                      {(division.metadata?.geo as string) || '-'}
                    </MDTypography>
                  </MDBox>
                </Grid>

                {showMetadata && (
                  <Grid item xs={12}>
                    <Card sx={{ p: 3 }}>
                      <JSONEditor
                        data={division.metadata}
                        title="Division Metadata"
                        height="h-64"
                        onSave={async (newMetadata) => {
                          // Update division metadata using the existing updateDivision hook
                          await updateDivision({
                            name: division.name,
                            metadata: newMetadata as Record<string, unknown>,
                          });
                        }}
                      />
                    </Card>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Card sx={{ p: 3 }}>
                    <AliasCrudTable
                      title="Aliases"
                      description={`These combinations map to ${division.name}. Use dots (.) to separate parts.`}
                      aliases={aliases}
                      isLoading={isLoadingAliases}
                      onCreateAlias={handleCreateAlias}
                      onUpdateAlias={handleUpdateAlias}
                      onDeleteAlias={handleDeleteAlias}
                      emptyMessage="No aliases found for this division"
                      aliasLabel="Alias"
                      formatAlias={formatAlias}
                      parseAlias={parseAlias}
                    />
                  </Card>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <DivisionModal
        isOpen={isEditModalOpen}
        onClose={handleModalClose}
        division={division}
        onSuccess={handleModalSuccess}
      />
    </DashboardLayout>
  );
};

export default DivisionDetailPage;
